import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getNavigationSettings } from "./api";
import { getAllNavigationItemIds } from "./navigationConfig";

const NavigationSettingsContext = createContext({
    navigationItems: {},
    shortcuts: [],
    loading: false,
    error: null,
    refresh: () => {},
});

const DEFAULT_VISIBILITY = getAllNavigationItemIds().reduce((acc, id) => {
    acc[id] = true;
    return acc;
}, {});

export function NavigationSettingsProvider({ children }) {
    const [navigationItems, setNavigationItems] = useState(DEFAULT_VISIBILITY);
    const [shortcuts, setShortcuts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getNavigationSettings();
            setNavigationItems({ ...DEFAULT_VISIBILITY, ...(data?.navigationItems ?? {}) });
            setShortcuts(Array.isArray(data?.shortcuts) ? data.shortcuts : []);
            setError(null);
        } catch (err) {
            console.error("Failed to load navigation settings", err);
            setError(err);
            setNavigationItems(DEFAULT_VISIBILITY);
            setShortcuts([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const value = useMemo(
        () => ({ navigationItems, shortcuts, loading, error, refresh }),
        [navigationItems, shortcuts, loading, error, refresh],
    );

    return <NavigationSettingsContext.Provider value={value}>{children}</NavigationSettingsContext.Provider>;
}

export function useNavigationSettings() {
    return useContext(NavigationSettingsContext);
}
